import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Descriptions, Empty, Space, Spin, Table, Tag, Tooltip, Typography, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import PatientResultsService from '../../../services/PatientResultsService';

const { Text } = Typography;

const FLAG_TAGS = {
  normal: { label: 'In range', color: 'green' },
  borderline: { label: 'Borderline', color: 'gold' },
  high: { label: 'High', color: 'red' },
  low: { label: 'Low', color: 'red' },
};

const fmtDate = (d) => {
  if (!d) return '';
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

const columns = [
  {
    title: 'Biomarker',
    dataIndex: 'name',
    key: 'name',
    render: (name, r) => (
      <>
        <div style={{ fontWeight: 600 }}>{name}</div>
        {r.about && <Text type="secondary" style={{ fontSize: 12 }}>{r.about}</Text>}
      </>
    ),
  },
  {
    title: 'Result',
    key: 'value',
    render: (_, r) => (
      <span><strong>{r.value}</strong> <Text type="secondary">{r.units}</Text></span>
    ),
  },
  { title: 'Reference range', dataIndex: 'range', key: 'range', render: (v) => v || '—' },
  {
    title: 'Flag',
    dataIndex: 'flag',
    key: 'flag',
    render: (f) => (FLAG_TAGS[f] ? <Tag color={FLAG_TAGS[f].color}>{FLAG_TAGS[f].label}</Tag> : '—'),
  },
];

const AccountTag = ({ account, hasEmail }) => {
  if (!hasEmail) return <Tag>No email on file</Tag>;
  if (!account?.exists) {
    return <Tooltip title="They haven't created a Deployed Hearts account with this email yet"><Tag>No Deployed Hearts account</Tag></Tooltip>;
  }
  if (!account.verified) {
    return <Tooltip title="Results stay hidden until they confirm their email"><Tag color="gold">Account not verified</Tag></Tooltip>;
  }
  return <Tag color="green">Deployed Hearts account verified</Tag>;
};

const ReportCard = ({ report }) => {
  const { status = {}, summary = {}, results = [] } = report;
  const ready = status.phase === 'ready';
  const timeline = [
    ['Collected', report.collectedAt],
    ['Received by lab', report.sampleReceivedAt],
    ['Reported', report.resultsReadyAt],
  ].filter(([, d]) => d);

  return (
    <Card
      size="small"
      title={report.title}
      extra={<Tag>{status.label || report.labStatus || 'Pending'}</Tag>}
      style={{ marginBottom: 16 }}
    >
      {(timeline.length > 0 || report.orderingPhysician) && (
        <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }} style={{ marginBottom: 12 }}>
          {timeline.map(([label, d]) => (
            <Descriptions.Item key={label} label={label}>{fmtDate(d)}</Descriptions.Item>
          ))}
          {report.orderingPhysician && (
            <Descriptions.Item label="Ordering physician">{report.orderingPhysician}</Descriptions.Item>
          )}
        </Descriptions>
      )}

      {ready ? (
        <>
          <Space wrap size={[8, 8]} style={{ marginBottom: 12 }}>
            <Tag color="green">{summary.inRange || 0} in range</Tag>
            <Tag color="gold">{summary.borderline || 0} borderline</Tag>
            <Tag color="red">{summary.outOfRange || 0} out of range</Tag>
          </Space>
          <Table
            rowKey={(r) => r.biomarker}
            size="small"
            pagination={false}
            dataSource={results}
            columns={columns}
            scroll={{ x: true }}
          />
        </>
      ) : (
        <Text type="secondary">{status.message || 'Results are not ready yet.'}</Text>
      )}
    </Card>
  );
};

/** Member's lab kit results, shown the way the member sees them on Deployed Hearts. */
const MemberLabResultsTab = ({ patientId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [opening, setOpening] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await PatientResultsService.getMemberResults(patientId);
      if (!res?.success) throw new Error(res?.message || 'Failed to load results');
      setData(res.data);
    } catch (err) {
      setError(err.message || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const viewAsMember = async () => {
    // Open the tab synchronously so the popup blocker allows it, then point it at the link.
    const win = window.open('', '_blank');
    setOpening(true);
    try {
      const res = await PatientResultsService.impersonateMember(patientId);
      if (!res?.success) throw new Error(res?.message || 'Could not open the member view');
      if (win) {
        win.opener = null;
        win.location.href = res.data.url;
      } else {
        window.location.assign(res.data.url);
      }
    } catch (err) {
      win?.close();
      message.error(err.message || 'Could not open the member view');
    } finally {
      setOpening(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
  if (error) return <Alert type="error" showIcon message={error} action={<Button size="small" onClick={load}>Retry</Button>} />;

  const reports = data?.reports || [];

  return (
    <div style={{ padding: '8px 0' }}>
      <Space wrap style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <AccountTag account={data?.account} hasEmail={data?.hasEmail} />
        <Space>
          <Button icon={<FontAwesomeIcon icon={faRotateRight} />} onClick={load}>Reload</Button>
          <Tooltip title="Opens their Deployed Hearts results page in a new tab, read-only, for 15 minutes">
            <Button
              type="primary"
              icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
              onClick={viewAsMember}
              loading={opening}
              disabled={!data?.hasEmail}
            >
              View as member
            </Button>
          </Tooltip>
        </Space>
      </Space>

      {reports.length === 0 ? (
        <Empty description="No lab kits or orders for this member yet" />
      ) : (
        reports.map((report) => <ReportCard key={report.id} report={report} />)
      )}
    </div>
  );
};

export default MemberLabResultsTab;
